import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { getPageName, getUiInterface, getSuccessMessage, getErrorMessage, getWordConnector } from "../../../locales/translationUtils";
import { useTranslation } from 'react-i18next';
import { useClient } from "../../../apis/client/ClientProvider";
import { useBranch } from "../../../apis/branch/BranchProvider";
import { useSupplier } from "../../../apis/supplier/SupplierProvider";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import ConfirmDeleteModal from "../../../components/ConfirmDeleteModal";
import Client from "../../../types/client";
import ColorPicker from "../../../components/ColorPicker";
import { useDestination } from "../../../apis/destination/DestinationProvider";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";

const ViewClientById: React.FC = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState<{
        message: string;
        variant: "success" | "danger" | "warning" | "info";
    } | null>(null);
    const { id } = useParams<{ id: string }>();
    const { fetchClientById, excluiClient, putClient } = useClient();
    const { fetchBranches, branches } = useBranch();
    const { fetchSuppliers, suppliers } = useSupplier();
    const { fetchDestination, destinations } = useDestination();
    const [client, setClient] = useState<Client | null>(null);
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedBranches, setSelectedBranches] = React.useState([] as any[]);
    const [selectedSuppliers, setSelectedSuppliers] = React.useState([] as any[]);
    const [selectedDestinations, setSelectedDestinations] = React.useState([] as any[]);
    const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());

    const navItems: NavBarItem[] = [
        { label: t('client.labels.home'), route: "/", iconName: "house-door" },
        { label: t('client.labels.list'), route: "/Client", iconName: "list" },
        { label: t('client.labels.new'), route: "/Client/create", iconName: "plus-circle" },
    ];

    useEffect(() => {
        if (!id) throw new Error("ID inválido");

        const loadClient = async () => {
            try {
                setLoading(true);
                setError(false);

                if (!id) throw new Error("ID inválido");

                const data = await fetchClientById(Number(id));

                await Promise.all([
                    fetchBranches(null),
                    fetchSuppliers(null),
                    fetchDestination(null)
                ]);

                setClient(data);
                
                if (data?.branches) {
                    setSelectedBranches(data.branches.map((branch: any) => branch.id));
                }
                if (data?.suppliers) {
                    setSelectedSuppliers(data.suppliers.map((supplier: any) => supplier.id));
                }
                if (data?.destinations) {
                    setSelectedDestinations(data.destinations.map((destination: any) => destination.id));
                }
            } catch (err) {
                console.error("Erro ao carregar os dados:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        loadClient();
    }, [id, fetchClientById, fetchBranches, fetchSuppliers, fetchDestination]);

    const handleEdit = async () => {
        if (!client) return;

        if (isEditing && client.id) {
            try {
                const nullRequiredFields: string[] = [];
        
                if (!client.name) nullRequiredFields.push("Name");
                if (!client.location?.latitude) nullRequiredFields.push("Lat");
                if (!client.location?.longitude) nullRequiredFields.push("Lng");
                if (!client.location?.address?.street) nullRequiredFields.push("Street");
                if (!client.location?.address?.number) nullRequiredFields.push("Number");
                if (!client.location?.address?.complement) nullRequiredFields.push("Complement");
                if (!client.location?.address?.city) nullRequiredFields.push("City");
                if (!client.location?.address?.state) nullRequiredFields.push("State");
                if (!client.location?.address?.country) nullRequiredFields.push("Country");
                if (!client.location?.address?.zipCode) nullRequiredFields.push("Zip Code");

                if (nullRequiredFields.length > 0) {
                    setInvalidFields(new Set(nullRequiredFields));
                    const warnMessage: string =
                        nullRequiredFields.length === 1
                            ? getUiInterface("fieldRequiredSingle", {
                                field: nullRequiredFields[0],
                            })
                            : getUiInterface("fieldRequiredMultiple", {
                                fields: `${nullRequiredFields.slice(0, -1).join(", ")}${nullRequiredFields.length > 1
                                    ? ` ${getWordConnector("and")} `
                                    : ""
                                    }${nullRequiredFields.slice(-1)}`,
                            });

                    setAlert({ message: warnMessage, variant: "danger" });
                    return;
                } else {
                    setInvalidFields(new Set());
                }

                const sucesso = await putClient(client.id, client);

                if (sucesso) {
                    const successMessage = getSuccessMessage("editClient", { id: client.id, });
                    navigate(`/Client`, {
                        state: {
                            message: successMessage,
                            variant: "success",
                        },
                    });
                }
                else {
                    const errorMessage = getErrorMessage("editClientError", { id: client.id, });
                    setAlert({ message: errorMessage, variant: "danger" });
                }
            } catch (error) {
                setAlert({ message: "Erro ao atualizar os dados.", variant: "danger" });
            }
        } else {
            setIsEditing(true);
        }
    };


    const handleDelete = async () => {
        if (!id) return;

        try {
            const sucesso = await excluiClient(Number(id));

            if (sucesso) {
                const successMessage = getSuccessMessage("deleteClient", { id: id });
                navigate(`/client`, {
                    state: {
                        message: successMessage,
                        variant: "success",
                    },
                });
            }
            else {
                const errorMessage = getErrorMessage("deleteClientError", { id: id, });
                setAlert({ message: errorMessage, variant: "danger" });
            }
        } catch (error) {
            setAlert({ message: "Erro ao excluir o registro.", variant: "danger" });
        }
    };


    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (client) {
            const { name, value } = e.target;
    
            setClient((prevClient: any) => {
                const keys = name.split(".");
                const updatedClient = { ...prevClient };
                let current: any = updatedClient;
    
                for (let i = 0; i < keys.length - 1; i++) {
                    const key = keys[i];
                    current[key] = { ...current[key] };
                    current = current[key];
                }
    
                current[keys[keys.length - 1]] = value;
                
                return updatedClient;
            });
        }
    };

    const hadleChangeColor = (newColor: string) => {
        setClient((prevState: any) => ({
            ...prevState,
            color: newColor.replaceAll("#", ""),
        }));
    };

    const handleSelectionBranchesChange = (selectionModel: any) => {
        setSelectedBranches(selectionModel);
        setClient((prevValues: any) => {
            return {
                ...prevValues,
                branches: branches?.filter((branch: any) => selectionModel.includes(branch.id))
            }
        })
    };

    const handleSelectionSuppliersChange = (selectionModel: any) => {
        setSelectedSuppliers(selectionModel);
        setClient((prevValues: any) => {
            return {
                ...prevValues,
                suppliers: suppliers?.filter((supplier: any) => selectionModel.includes(supplier.id))
            }
        })
    };

    const handleSelectionDestinationsChange = (selectionModel: any) => {
        setSelectedDestinations(selectionModel);
        setClient((prevValues: any) => {
            return {
                ...prevValues,
                destinations: destinations?.filter((destination: any) => selectionModel.includes(destination.id))
            }
        })
    };

    const companiesColumns: GridColDef[] = [
        { field: "id", headerName: t('client.fields.id'), width: 80,},
        { field: "cnpj", headerName: t('client.fields.cnpj'), width: 200 },
        { field: "name", headerName: t('client.fields.name'), width: 350 },
    ];

    const destinationsColumns: GridColDef[] = [
        { field: "id", headerName: t('destination.fields.id'), width: 80,},
        { field: "description", headerName: t('destination.fields.description'), width: 200 },
        { field: "kind", headerName: t('destination.fields.kind'), width: 350 },
    ];

    if (loading) {
        return <div className="text-center mt-4">Carregando...</div>;
    }

    if (error || !client) {
        return <div className="text-center mt-4 text-danger">Erro ao carregar os dados.</div>;
    }

    return (
        <Container fluid>
            <NavBar items={navItems} />
            {alert && (
                <AlertOverlay
                    message={alert.message}
                    variant={alert.variant}
                    onClose={() => setAlert(null)}
                />
            )}
            <div className="mb-3"></div>
            <h2>{isEditing ? getPageName("editClient") : getPageName("viewClient")}</h2>

            {successMessage && (
                <div style={{ fontSize: "14px", color: "green", marginTop: "5px" }}>
                    {successMessage}
                </div>
            )}

            <Form className="mt-3">
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group>
                            <Form.Label>{t('client.fields.id')}</Form.Label>
                            <Form.Control size="sm" readOnly value={client.id} style={{ width: "100%", backgroundColor: "#f0f0f0" }} />
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col md={4} className="mb-3">
                        <Form.Group controlId="name">
                            <Form.Label>{t('client.fields.name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={client.name}
                                isInvalid={invalidFields.has("Name")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="cnpj">
                            <Form.Label>{t('client.fields.cnpj')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="cnpj"
                                value={client.cnpj}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>

                    <Col md={4} className="mb-3">
                        <Form.Group controlId="color">
                            <Form.Label>{t('client.fields.color')}</Form.Label>
                            <div style={{display: "flex", alignItems: "center"}}>
                                <Form.Control
                                    type="text"
                                    name="color"
                                    value={client.color}
                                    onChange={handleChange}
                                    readOnly={!isEditing}
                                    style={{ 
                                        width: "calc(100% - 64px)", 
                                        display: "inline-block", 
                                        marginRight: "8px",
                                        backgroundColor: isEditing ? "white" : "#f0f0f0"
                                    }}
                                />
                                <ColorPicker
                                    color={`#${client.color}`}
                                    onChange={(newColor) =>
                                        hadleChangeColor(newColor)
                                    }
                                    blocked={!isEditing}
                                />
                            </div>
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <h5>{t('client.labels.location')}</h5>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lat">
                            <Form.Label>{t('client.fields.lat')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.latitude"
                                value={client.location?.latitude}
                                isInvalid={invalidFields.has("Lat")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="lng">
                            <Form.Label>{t('client.fields.lng')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.longitude"
                                value={client.location?.longitude}
                                isInvalid={invalidFields.has("Lng")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>{t('client.labels.address')}</h6>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="street">
                            <Form.Label>{t('client.fields.street')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.street"
                                value={client.location?.address?.street}
                                isInvalid={invalidFields.has("Street")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="mb-3">
                        <Form.Group controlId="number">
                            <Form.Label>{t('client.fields.number')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.number"
                                value={client.location?.address?.number}
                                isInvalid={invalidFields.has("Number")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={5} className="mb-3">
                        <Form.Group controlId="complement">
                            <Form.Label>{t('client.fields.complement')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.complement"
                                value={client.location?.address?.complement}
                                isInvalid={invalidFields.has("Complement")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="city">
                            <Form.Label>{t('client.fields.city')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.city"
                                value={client.location?.address?.city}
                                isInvalid={invalidFields.has("City")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="state">
                            <Form.Label>{t('client.fields.state')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.state"
                                value={client.location?.address?.state}
                                isInvalid={invalidFields.has("State")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                
                <Row>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="country">
                            <Form.Label>{t('client.fields.country')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.country"
                                value={client.location?.address?.country}
                                isInvalid={invalidFields.has("Country")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6} className="mb-3">
                        <Form.Group controlId="zipCode">
                            <Form.Label>{t('client.fields.zipCode')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="location.address.zipCode"
                                value={client.location?.address?.zipCode}
                                isInvalid={invalidFields.has("Zip Code")}
                                onChange={handleChange}
                                readOnly={!isEditing}
                                style={{ width: "100%", backgroundColor: isEditing ? "white" : "#f0f0f0" }}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <h6>{t('client.labels.branches')}</h6>
                    <Col md={12} className="mb-3">
                        <DataGrid
                            rows={(isEditing)? (branches || []): client.branches}
                            columns={companiesColumns}
                            checkboxSelection={isEditing}
                            onRowSelectionModelChange={handleSelectionBranchesChange}
                            rowSelectionModel={(isEditing)? (selectedBranches || []): []}
                            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                            sx={{
                                    "& .MuiDataGrid-row:nth-of-type(even)": {
                                    backgroundColor: "#f5f5f5",
                                    },
                                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                                    backgroundColor: "white",
                                    },
                                    "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "#b2d1ff",
                                },
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "14px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "14px",
                                },
                            }}
                        />
                    </Col>
                </Row>

                <Row>
                    <h6>{t('client.labels.suppliers')}</h6>
                    <Col md={12} className="mb-3">
                        <DataGrid
                            rows={(isEditing)? (suppliers || []): client.suppliers}
                            columns={companiesColumns}
                            checkboxSelection={isEditing}
                            onRowSelectionModelChange={handleSelectionSuppliersChange}
                            rowSelectionModel={(isEditing)? (selectedSuppliers || []): []}
                            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                            sx={{
                                    "& .MuiDataGrid-row:nth-of-type(even)": {
                                    backgroundColor: "#f5f5f5",
                                    },
                                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                                    backgroundColor: "white",
                                    },
                                    "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "#b2d1ff",
                                },
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "14px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "14px",
                                },
                            }}
                        />
                    </Col>
                </Row>

                <Row>
                    <h6>{t('client.labels.destinations')}</h6>
                    <Col md={12} className="mb-3">
                        <DataGrid
                            rows={(isEditing)? (destinations || []): client.destinations}
                            columns={destinationsColumns}
                            checkboxSelection={isEditing}
                            onRowSelectionModelChange={handleSelectionDestinationsChange}
                            rowSelectionModel={(isEditing)? (selectedDestinations || []): []}
                            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                            sx={{
                                    "& .MuiDataGrid-row:nth-of-type(even)": {
                                    backgroundColor: "#f5f5f5",
                                    },
                                    "& .MuiDataGrid-row:nth-of-type(odd)": {
                                    backgroundColor: "white",
                                    },
                                    "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "#b2d1ff",
                                },
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "14px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "14px",
                                },
                            }}
                        />
                    </Col>
                </Row>
            </Form>

            <div className="mt-3 mb-3 d-flex gap-2">
                <Button variant="primary" onClick={handleEdit}>
                    {isEditing ? getUiInterface("save") : getUiInterface("edit")}
                </Button>
                <Button variant="danger" onClick={() => setShowDeleteModal(true)}>{getUiInterface("delete")}</Button>
            </div>

            <ConfirmDeleteModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
            />
        </Container>
    );
};

export default ViewClientById;
